import { IssueScopeKind } from '../../../../shared/issuesV2';
import Locator from '../../../../shared/Locator';
import { useIssueTypesSummary } from '../issues/useIssueTypesSummary';
import type { ProjectScope } from '../../../../shared/issuesV2';

type UseRevisionScanIssueTypesParams = {
  revisionLocator: string;
  revisionScanId?: number;
  shouldFetch?: boolean;
};

/**
 * Hook for fetching issue type summary for a specific revision scan.
 *
 * This is a specialized wrapper around `useIssueTypesSummary` for the revision scan use case.
 * It constructs a ProjectScope from the revision locator and scan ID, then fetches the issue counts.
 *
 * **Important**: This hook only fetches the data. The calling component is responsible for:
 * 1. Dispatching to Redux IssuesV2 store: `dispatch(IssuesV2Store.actions.setScanId(revisionScanId))`
 * 2. Updating the URL query param: `routerPush({ pathname: window.location.pathname, search: '?revisionScanId=${revisionScanId}' })`
 *
 * @param params - Parameters for fetching issue types for a revision scan
 * @param params.revisionLocator - The full revision locator (e.g., "git+github.com/owner/repo$abc123")
 * @param params.revisionScanId - The ID of the revision scan
 * @param params.shouldFetch - Whether to fetch the query (default: true)
 *
 * @example
 * ```typescript
 * const dispatch = useDispatch();
 * const { data: issueTypesSummary, isLoading } = useRevisionScanIssueTypes({
 *   revisionLocator: revision.data.locator,
 *   revisionScanId: selectedScanId,
 * });
 *
 * // When the data is available, update Redux and URL
 * useEffect(() => {
 *   if (issueTypesSummary) {
 *     dispatch(IssuesV2Store.actions.setScanId(revisionScanId));
 *     routerPush({ pathname: window.location.pathname, search: `?revisionScanId=${revisionScanId}` });
 *   }
 * }, [issueTypesSummary]);
 * ```
 */
export function useRevisionScanIssueTypes({
  revisionLocator,
  revisionScanId,
  shouldFetch = true,
}: UseRevisionScanIssueTypesParams) {
  const locator = new Locator(revisionLocator);

  const scope: ProjectScope = {
    type: IssueScopeKind.Project,
    id: locator.toPackageString(),
    revision: locator.revision.value || '',
    revisionScanId,
  };

  return useIssueTypesSummary(scope, shouldFetch);
}
